import {
  Box,
  Button,
  Spacer,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useDisclosure,
  VStack,
  HStack,
  Heading,
  Text,
  SimpleGrid,
  Center,
} from "@chakra-ui/react";
import React, { useState, useMemo, useEffect } from "react";
import ConfigurationTable from "../../components/tables/ConfigurationTable";
import DepartmentTable from "../../components/tables/DepartmentTable";
import ModalComponent from "../../components/modal";
import AddLocation from "../../parts/configuration/addLocation";
import {
  useEditDepartment,
  useEditLocation,
  useEditReportTo,
  useFetchDepartments,
  useFetchLocations,
  useFetchReportTo,
  useFetchShift,
  useFetchHolidays,
  useAddHoliday,
  useEditHoliday,
  useDeleteHoliday,
  useBulkImportHolidays,
} from "../../services/configurationService";
import AddDepartment from "../../parts/configuration/addDepartment";
import ReportingSeniorTable from "../../components/tables/reportingSeniorTable";
import AddReportTo from "../../parts/configuration/addReportTo";
import ShiftTable from "../../components/tables/shiftTable";
import AddShift from "../../parts/configuration/addShift";
import HolidaysTable from "../../components/tables/HolidaysTable";
import AddHoliday from "../../parts/configuration/addHoliday";
import BulkImportHolidays from "../../parts/configuration/bulkImportHolidays";
import { FiMapPin, FiUsers, FiUserCheck, FiClock, FiPlus, FiCalendar, FiUpload, FiLock, FiCoffee } from "react-icons/fi";
import { useUserContext } from "../../context/UserContext";
import BreakSettingsTab from "../../parts/configuration/BreakSettingsTab";

const Configuration = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [todo, setTodo] = useState("");
  const [editConfig, setEditConfig] = useState({});
  const [config, setConfig] = useState("Location");
  
  // Get user context for role-based access
  const { userRole, isAdmin } = useUserContext();
  const isBranchUser = !isAdmin;

  // Handle hash parameter to open specific tab
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#shift') {
      setConfig("Shift");
    } else if (hash === '#location') {
      setConfig("Location");
    } else if (hash === '#department') {
      setConfig("Department");
    } else if (hash === '#reporting-senior') {
      setConfig("Reporting Senior");
    } else if (hash === '#holiday' && isAdmin) {
      setConfig("Holiday");
    } else if (hash === '#break-settings') {
      setConfig("Break Settings");
    }
  }, [isAdmin]);

  // Prevent branch users from accessing Holiday tab
  useEffect(() => {
    if (isBranchUser && config === "Holiday") {
      setConfig("Location");
    }
  }, [isBranchUser, config]);

  const { getDepartmentData, refetchGetDepartment, getDepartmentIsLoading } =
    useFetchDepartments({ enabled: true });
  const { getLocationsData, getLocationsIsLoading, refetchGetLocations } =
    useFetchLocations({ enabled: true });
  const { editLocationPayload, editLocationIsLoading } = useEditLocation(
    (res) => {
      refetchGetLocations();
      onClose();
    }
  );
  const { editDepartmentPayload, editDepartmentIsLoading } = useEditDepartment((res) => {
    refetchGetDepartment();
    onClose();
  });
  const { getReportToData, getReportToIsLoading, refetchGetReportTo } = useFetchReportTo({ enabled: true });
  const { editReportToPayload, editReportToIsLoading } = useEditReportTo((res) => {
    refetchGetReportTo();
    onClose();
  });
  const { refetchGetShift, getShiftData, getShiftIsLoading } = useFetchShift({ enabled: true });
  const { getHolidaysData, getHolidaysIsLoading, refetchGetHolidays } = useFetchHolidays({ enabled: true });

  // Calculate configuration statistics
  const configStats = useMemo(() => {
    return {
      locations: getLocationsData?.length || 0,
      departments: getDepartmentData?.length || 0,
      reportingSeniors: getReportToData?.length || 0,
      shifts: getShiftData?.length || 0,
      holidays: getHolidaysData?.length || 0,
    };
  }, [getLocationsData, getDepartmentData, getReportToData, getShiftData, getHolidaysData]);

  const handleEdit = (item) => {
    setTodo("edit");
    onOpen();
    setEditConfig(item);
  };

  const handleAddNew = () => {
    setTodo("new");
    setEditConfig({});
    onOpen();
  };

  // Holidays hooks
  const { addHolidayPayload, addHolidayIsLoading } = useAddHoliday((res) => {
    refetchGetHolidays();
    onClose();
  });

  const { editHolidayPayload, editHolidayIsLoading } = useEditHoliday((res) => {
    refetchGetHolidays();
    onClose();
  });

  const { deleteHolidayPayload, deleteHolidayIsLoading } = useDeleteHoliday((res) => {
    refetchGetHolidays();
  });

  const { bulkImportHolidaysPayload, bulkImportHolidaysIsLoading } = useBulkImportHolidays((res) => {
    refetchGetHolidays();
    onClose();
  });

  const handleDeleteHoliday = async (holidayId) => {
    try {
      await deleteHolidayPayload(holidayId);
    } catch (error) {
      console.error("Error deleting holiday:", error);
    }
  };

  // Access control for branch users - restrict holidays only
  const handleHolidayAccess = () => {
    if (isBranchUser && config === "Holiday") {
      return (
        <Box w="100%" bg="gray.50" minH="50vh" p={6}>
          <Center h="40vh">
            <VStack spacing={6} maxW="md" textAlign="center">
              <Box
                p={6}
                borderRadius="16px"
                bg="white"
                shadow="sm"
                border="1px solid"
                borderColor="gray.200"
              >
                <FiLock size={48} color="#E53E3E" />
                <Heading size="lg" color="gray.900" fontWeight="600" mt={4}>
                  Access Restricted
                </Heading>
                <Text color="gray.600" fontSize="md" mt={2}>
                  Holiday management is restricted to administrators only.
                </Text>
                <Text color="gray.500" fontSize="sm" mt={2}>
                  Please contact your administrator if you need to manage holidays.
                </Text>
              </Box>
            </VStack>
          </Center>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box w="100%" bg="gray.50" minH="100vh">
      {/* Page Header */}
      <VStack spacing={6} p={6} align="stretch">
        <Heading size="lg" color="gray.900" fontWeight="600">
          System Configuration
        </Heading>
        <Text color="gray.600" fontSize="md">
          Manage locations, departments, reporting seniors, and shifts
        </Text>
      </VStack>

      {/* Stats Cards */}
      <VStack spacing={6} px={6}>
                 <SimpleGrid columns={{ base: 1, md: 2, lg: isAdmin ? 6 : 5 }} spacing={6} w="100%">
          <StatCard
            title="Locations"
            value={configStats.locations}
            icon={<FiMapPin />}
            color="blue"
          />
          <StatCard
            title="Departments"
            value={configStats.departments}
            icon={<FiUsers />}
            color="green"
          />
          <StatCard
            title="Reporting Seniors"
            value={configStats.reportingSeniors}
            icon={<FiUserCheck />}
            color="purple"
          />
          <StatCard
            title="Shifts"
            value={configStats.shifts}
            icon={<FiClock />}
            color="orange"
          />
          <StatCard
            title="Break Settings"
            value="—"
            icon={<FiCoffee />}
            color="teal"
          />
                     {/* Holidays Stats - Admin only */}
           {isAdmin && (
             <StatCard
               title="Holidays"
               value={configStats.holidays}
               icon={<FiCalendar />}
               color="purple"
             />
           )}
        </SimpleGrid>

        {/* Configuration Tabs */}
        <Box
          bg="white"
          borderRadius="16px"
          shadow="sm"
          border="1px solid"
          borderColor="gray.200"
          overflow="hidden"
          w="100%"
        >
          <Tabs variant="soft-rounded" colorScheme="blue" p={6}>
            <TabList mb={6} flexWrap="wrap" gap={2}>
              <Tab
                onClick={() => setConfig("Location")}
                _selected={{ bg: "blue.500", color: "white" }}
                borderRadius="12px"
              >
                <HStack spacing={2}>
                  <FiMapPin />
                  <Text>Location</Text>
                </HStack>
              </Tab>
              <Tab
                onClick={() => setConfig("Department")}
                _selected={{ bg: "blue.500", color: "white" }}
                borderRadius="12px"
              >
                <HStack spacing={2}>
                  <FiUsers />
                  <Text>Department</Text>
                </HStack>
              </Tab>
              <Tab
                onClick={() => setConfig("Reporting Senior")}
                _selected={{ bg: "blue.500", color: "white" }}
                borderRadius="12px"
              >
                <HStack spacing={2}>
                  <FiUserCheck />
                  <Text>Reporting Senior</Text>
                </HStack>
              </Tab>
              <Tab
                onClick={() => setConfig("Shift")}
                _selected={{ bg: "blue.500", color: "white" }}
                borderRadius="12px"
              >
                <HStack spacing={2}>
                  <FiClock />
                  <Text>Shift</Text>
                </HStack>
              </Tab>
              <Tab
                onClick={() => setConfig("Break Settings")}
                _selected={{ bg: "blue.500", color: "white" }}
                borderRadius="12px"
              >
                <HStack spacing={2}>
                  <FiCoffee />
                  <Text>Break Settings</Text>
                </HStack>
              </Tab>
                             {/* Holiday Tab - Admin only */}
               {isAdmin && (
                 <Tab
                   onClick={() => setConfig("Holiday")}
                   _selected={{ bg: "blue.500", color: "white" }}
                   borderRadius="12px"
                 >
                   <HStack spacing={2}>
                     <FiCalendar />
                     <Text>Holiday</Text>
                   </HStack>
                 </Tab>
               )}

              <Spacer />
              <HStack spacing={2}>
                {config === "Holiday" && (
                  <Button
                    leftIcon={<FiUpload />}
                    colorScheme="green"
                    variant="outline"
                    onClick={() => {
                      setTodo("bulk-import");
                      onOpen();
                    }}
                    borderRadius="12px"
                    size="md"
                  >
                    Bulk Import
                  </Button>
                )}
                {config !== "Break Settings" && (
                  <Button
                    leftIcon={<FiPlus />}
                    colorScheme="blue"
                    variant="solid"
                    onClick={handleAddNew}
                    borderRadius="12px"
                    size="md"
                  >
                    Add {config}
                  </Button>
                )}
              </HStack>
            </TabList>

                         <TabPanels>
               {/* Show access restricted message for branch users trying to access Holiday tab */}
               {handleHolidayAccess()}
               
               <TabPanel p={0}>
                 <ConfigurationTable
                   configurationData={getLocationsData}
                   editLocationIsLoading={editLocationIsLoading}
                   handleEdit={handleEdit}
                   refetchGetLocations={refetchGetLocations}
                   config={config}
                   getLocationsIsLoading={getLocationsIsLoading}
                 />
               </TabPanel>
              <TabPanel p={0}>
                <DepartmentTable
                  configurationData={getDepartmentData}
                  config={config}
                  handleEdit={handleEdit}
                  refetchGetDepartment={refetchGetDepartment}
                  getDepartmentIsLoading={getDepartmentIsLoading}
                />
              </TabPanel>
              <TabPanel p={0}>
                <ReportingSeniorTable
                  configurationData={getReportToData}
                  config={config}
                  handleEdit={handleEdit}
                  getReportToIsLoading={getReportToIsLoading}
                  refetchGetReportTo={refetchGetReportTo}
                />
              </TabPanel>
              <TabPanel p={0}>
                <ShiftTable
                  configurationData={getShiftData}
                  config={config}
                  handleEdit={handleEdit}
                  getShiftIsLoading={getShiftIsLoading}
                  refetchGetShift={refetchGetShift}
                />
              </TabPanel>
              <TabPanel p={0}>
                <Box p={6}>
                  <BreakSettingsTab />
                </Box>
              </TabPanel>
                             {/* Holiday Panel - Admin only */}
               {isAdmin && (
                 <TabPanel p={0}>
                   <HolidaysTable
                     holidaysData={getHolidaysData}
                     handleEdit={handleEdit}
                     handleDelete={handleDeleteHoliday}
                     getHolidaysIsLoading={getHolidaysIsLoading}
                     refetchGetHolidays={refetchGetHolidays}
                   />
                 </TabPanel>
               )}
            </TabPanels>
          </Tabs>
        </Box>
      </VStack>

      <ModalComponent
        isOpen={isOpen}
        onClose={() => false}
        size={"md"}
        title={
          todo === "bulk-import" 
            ? "Bulk Import Holidays" 
            : `${todo === "edit" ? "Edit" : "Add"} ${config?.toLowerCase()}`
        }
        closeBtn={true}
        closeModal={onClose}
      >
        {config === "Location" ? (
          <AddLocation
            closeModal={onClose}
            editLocation={editConfig}
            todo={todo}
            editLocationPayload={editLocationPayload}
            editLocationIsLoading={editLocationIsLoading}
            refetchGetLocations={refetchGetLocations}
          />
        ) : config === "Department" ? (
          <AddDepartment
            closeModal={onClose}
            editDept={editConfig}
            todo={todo}
            editDepartmentPayload={editDepartmentPayload}
            editDepartmentIsLoading={editDepartmentIsLoading}
            refetchGetDepartment={refetchGetDepartment}
          />
        ) : config === "Reporting Senior" ? (
          <AddReportTo
            closeModal={onClose}
            todo={todo}
            editReportTo={editConfig}
            refetchGetReportTo={refetchGetReportTo}
            editReportToIsLoading={editReportToIsLoading}
            editReportToPayload={editReportToPayload}
          />
        ) : config === "Shift" ? (
          <AddShift
            closeModal={onClose}
            editShift={editConfig}
            todo={todo}
            refetchGetShift={refetchGetShift}
          />
        ) : config === "Holiday" ? (
          todo === "bulk-import" ? (
            <BulkImportHolidays
              bulkImportHolidaysPayload={bulkImportHolidaysPayload}
              bulkImportHolidaysIsLoading={bulkImportHolidaysIsLoading}
              closeModal={onClose}
              refetchGetHolidays={refetchGetHolidays}
            />
          ) : (
            <AddHoliday
              closeModal={onClose}
              editHoliday={editConfig}
              todo={todo}
              addHolidayPayload={addHolidayPayload}
              editHolidayPayload={editHolidayPayload}
              addHolidayIsLoading={addHolidayIsLoading}
              editHolidayIsLoading={editHolidayIsLoading}
              refetchGetHolidays={refetchGetHolidays}
            />
          )
        ) : (
          ""
        )}
      </ModalComponent>
    </Box>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon, color }) => {
  const colorScheme = {
    blue: { bg: "blue.50", color: "blue.600", border: "blue.200" },
    green: { bg: "green.50", color: "green.600", border: "green.200" },
    red: { bg: "red.50", color: "red.600", border: "red.200" },
    orange: { bg: "orange.50", color: "orange.600", border: "orange.200" },
    purple: { bg: "purple.50", color: "purple.600", border: "purple.200" },
    teal: { bg: "teal.50", color: "teal.600", border: "teal.200" },
  };

  const colors = colorScheme[color] || colorScheme.blue;

  return (
    <Box
      bg="white"
      p={6}
      borderRadius="16px"
      shadow="sm"
      border="1px solid"
      borderColor="gray.200"
      _hover={{ shadow: "md" }}
      transition="all 0.2s"
    >
      <HStack spacing={4} align="center">
        <Box
          p={3}
          borderRadius="12px"
          bg={colors.bg}
          color={colors.color}
          border="1px solid"
          borderColor={colors.border}
        >
          {icon}
        </Box>
        <VStack spacing={1} align="start" flex={1}>
          <Text fontSize="sm" color="gray.600" fontWeight="500">
            {title}
          </Text>
          <Text fontSize="2xl" color="gray.900" fontWeight="700">
            {value}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
};

export default Configuration;
